import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AgGridReact } from "ag-grid-react";
import { Button, Form, Card } from 'react-bootstrap';
import { ClientSideRowModelModule } from 'ag-grid-community';
import { CTSearchView, AccessGridTable, StethoscopeLoading } from '../../components';
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-regular-svg-icons';
import { faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import {
    getUserDetailsGet,
    getUsersManagementReportsGet,
    getUserManagementDetailsGet,
    getGendersList,
    getRolesList,
    getSubscriptionsList,
    usersManagementDetailsUpdate,
    usersManagementArchive,
} from 'apiServices';
import defaultAvatar from '../../assets/images/logo/default-user.jpg';
import { Edit, Trash2, FileText, RotateCw } from 'feather-icons-react';
import { themeColors } from '../../styles/theme';
import './usersList.css';
import { showPmrSuccess } from '../../utils/swalUtils';

const customStyles = `
    .custom-select:focus,
    .custom-select:active,
    .custom-select:hover {
        border-color: #ced4da !important;
        box-shadow: none !important;
        outline: none !important;
    }
`;

const UsersList = () => {

    const [loading, setLoading] = useState(false);
    const [usersData, setUsersData] = useState([]);
    const dispatch = useDispatch();
    const [includeArchive, setIncludeArchive] = useState(false);
    const userData = useSelector((state) => state.apiService.login);
    const token = userData?.Token || '';
    const params = { token, user_id: userData.user_id };
    const [searchQuery, setSearchQuery] = useState('');
    const gridRef = useRef();
    const [showEditModal, setShowEditModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [gendersList, setGendersList] = useState([]);
    const [rolesList, setRolesList] = useState([]);
    const [subscriptionsList, setSubscriptionsList] = useState([]);
    const [formData, setFormData] = useState({
        active: false,
        token: token,
        user_id: userData.user_id,
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successType, setSuccessType] = useState("");

    // For pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(11);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedCount, setSelectedCount] = useState(0);

    useEffect(() => {
        dispatch(getUserDetailsGet(params))
            .then((response) => {
                console.log(response, "getUserDetailsGet");
            })
        dispatch(getUsersManagementReportsGet(params))
            .then((response) => {
                console.log(response, "getUsersManagementReportsGet");
            })
        dispatch(getUserManagementDetailsGet(params))
            .then((response) => {
                const Table = response.payload;
                if (Table && Array.isArray(Table)) {
                    const formattedData = Table.map(user => ({
                        id: user.UserID,
                        userName: user.UserName,
                        email: user.EmailAddress,
                        firstName: user.FirstName,
                        lastName: user.LastName,
                        phone: user.Phone,
                        userType: user.UserTypeName,
                        status: user.Status,
                        subscriptionType: user.SubscriptionType,
                        lastLoggedIn: user.LastLoggedIn,
                        accountNum: user.AccountNum,
                        address1: user.Address1,
                        address2: user.Address2,
                        city: user.City,
                        state: user.State,
                        zip: user.Zip,
                        country: user.Country,
                        dob: user.DOB,
                        gender: user.Gender,
                        imagePath: user.ImagePath,
                        locked: user.Locked
                    }));
                    setUsersData(formattedData);
                    setTotalCount(formattedData.length);
                }
            })
            .catch((error) => {
                console.error("Error fetching user management details:", error);
            })
    }, []);

    useEffect(() => {
        // Wait for grid to be rendered
        const interval = setInterval(() => {
            const gridDiv = document.querySelector('.ag-theme-balham .ag-body-viewport');
            if (gridDiv && gridRef.current && gridRef.current.api) {
                clearInterval(interval);
                const onScroll = () => {
                    const api = gridRef.current.api;
                    const rowHeight = 48; // your rowHeight
                    const scrollTop = gridDiv.scrollTop;
                    const firstVisibleRow = Math.floor(scrollTop / rowHeight);
                    const newPage = Math.floor(firstVisibleRow / pageSize) + 1;
                    if (currentPage !== newPage) {
                        setCurrentPage(newPage);
                        api.paginationGoToPage(newPage - 1);
                    }
                };
                gridDiv.addEventListener('scroll', onScroll);
                // Clean up
                return () => gridDiv.removeEventListener('scroll', onScroll);
            }
        }, 100);
        // Clean up interval on unmount
        return () => clearInterval(interval);
    }, [currentPage, pageSize]);

    // When currentPage changes by clicking pagination, scroll grid
    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.paginationGoToPage(currentPage - 1);
            // Scroll the viewport to top of the page
            const gridDiv = document.querySelector('.ag-theme-balham .ag-body-viewport');
            if (gridDiv) {
                gridDiv.scrollTop = (currentPage - 1) * pageSize * 48; // rowHeight * pageSize
            }
        }
    }, [currentPage, pageSize]);

    // Update selected count when row selection changes
    const onSelectionChanged = () => {
        if (gridRef.current && gridRef.current.api) {
            const selectedRows = gridRef.current.api.getSelectedRows();
            setSelectedCount(selectedRows.length);
        }
    };



    useEffect(() => {
        // Add custom styles to document
        const styleSheet = document.createElement("style");
        styleSheet.innerText = customStyles;
        document.head.appendChild(styleSheet);

        // Cleanup
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    const handleReportClick = (params) => {
        console.log(params);
    }

    const onClear = () => {
        setSearchQuery('');
    };

    // Custom cell renderer for Name with profile image
    const nameCellRenderer = (params) => (
        <div className="d-flex align-items-center">
            <img
                src={params.data.imagePath || defaultAvatar}
                alt="profile"
                className="user-avatar me-2"
                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
            />
            <span className="fw-bold text" style={{ cursor: 'pointer', color: 'var(--pmr-secondary)' }}>{params.value}</span>
        </div>
    );

    // Custom cell renderer for Gender
    const genderCellRenderer = (params) => (
        <span style={{ color: params.value === 'Male' ? 'var(--pmr-secondary)' : params.value === 'Female' ? '#e75480' : '#eb599c' }}>
            {params.value}
        </span>
    );

    // Custom cell renderer for Actions
    const actionsCellRenderer = (params) => (
        <>
            <span onClick={() => onEdit(params.data)} style={{ cursor: 'pointer', marginRight: 8 }}>
                <Edit color={themeColors.primary} size={20} />
            </span>
            <span onClick={() => onDelete(params.data)} style={{ cursor: 'pointer' }}>
                <Trash2 color="#d32f2f" size={20} />
            </span>
        </>
    );

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const onEdit = (row) => {
        setEditUser(row);
        setShowEditModal(true);
        console.log("Current editUser:", row);
        dispatch(getGendersList(params))
            .then((response) => {
                console.log("API Response:", response.payload, "getGendersList");
                const gendersList = response.payload.Table;
                if (gendersList && Array.isArray(gendersList)) {
                    console.log("Setting genders list:", gendersList);
                    setGendersList(gendersList);
                }
            })
            .catch((error) => {
                console.error("Error fetching genders list:", error);
            })

        dispatch(getRolesList(params))
            .then((response) => {
                console.log("API Response:", response.payload, "getRolesList");
                const rolesList = response.payload.Table;
                if (rolesList && Array.isArray(rolesList)) {
                    console.log("Setting roles list:", rolesList);
                    setRolesList(rolesList);
                }
            })
            .catch((error) => {
                console.error("Error fetching roles list:", error);
            })

        dispatch(getSubscriptionsList(params))
            .then((response) => {
                console.log("API Response:", response.payload, "getSubscriptionsList");
                const subscriptionsList = response.payload.Table;
                if (subscriptionsList && Array.isArray(subscriptionsList)) {
                    console.log("Setting subscriptions list:", subscriptionsList);
                    setSubscriptionsList(subscriptionsList);
                }
            })
            .catch((error) => {
                console.error("Error fetching subscriptions list:", error);
            })
    };

    const onDelete = (row) => {
        setUserToDelete(row);
        setShowDeleteModal(true);
    };

    const onReportView = (row) => {
        // Implement your report view logic here
        console.log('Report view clicked:', row);
    };

    const reportsCellRenderer = (params) => (
        <span onClick={() => onReportView(params.data)} style={{ cursor: 'pointer' }}>
            <FileText color="var(--pmr-secondary)" size={22} />
        </span>
    );

    const userColumns = [
        {
            headerName: "NAME",
            field: "userName",
            flex: 2,
            minWidth: 200,
            tooltipField: "userName",
            headerTooltip: "NAME",
            cellRenderer: nameCellRenderer
        },
        {
            headerName: "EMAIL",
            field: "email",
            flex: 2,
             tooltipField: "email",
            headerTooltip: "EMAIL",
            minWidth: 200
        },
        {
            headerName: "GENDER",
            field: "gender",
            flex: 1,
            minWidth: 100,
            tooltipField: "gender",
            headerTooltip: "GENDER",
            cellRenderer: genderCellRenderer
        },
        {
            headerName: "ROLE",
            field: "userType",
            flex: 0.7,
            tooltipField: "userType",
            headerTooltip: "ROLE",
            minWidth: 120,
            cellRenderer: (params) => (
                <span>
                    {params.value && params.value.toLowerCase() === 'admin' ? (
                        <FontAwesomeIcon icon={faShieldAlt} style={{ color: 'var(--pmr-danger)', fontSize: 15, marginRight: 4 }} />
                    ) : params.value && params.value.toLowerCase() === 'provider' ? (
                        <i className="fa fa-lg fa-user-md text me-1" style={{ fontSize: 15, color: 'var(--pmr-success)' }}></i>
                    ) : (
                        <FontAwesomeIcon icon={faUser} className="text-primary me-1" style={{ fontSize: 15 }} />
                    )}
                    {params.value}
                </span>
            )
        },
        {
            headerName: "LAST LOGGED IN",
            field: "lastLoggedIn",
            flex: 1,
            tooltipField: "lastLoggedIn",
            headerTooltip: "LAST LOGGED IN",
            minWidth: 120
        },
        {
            headerName: "SUBSCRIPTION",
            field: "subscriptionType",
            flex: 0.8,
            tooltipField: "subscriptionType",
            headerTooltip: "SUBSCRIPTION",
            minWidth: 180,
            cellRenderer: (params) => (
                <span className="badge bg-light text-primary border border-primary px-2 fw-bold" style={{ fontSize: 12, }}>{params.value}</span>
            )
        },
        {
            headerName: "STATUS",
            field: "status",
            flex: 0.8,
            tooltipField: "status",
            headerTooltip: "STATUS",
            minWidth: 100,
            cellRenderer: (params) => (
                <span
                    className="badge"
                    style={{
                        backgroundColor: params.value === 'active' ? 'var(--pmr-success)' : 'var(--pmr-danger)',
                        color: 'white',
                        borderRadius: 20,
                        // width: 80,
                        // height: 30,
                        fontSize: 12,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontWeight: 400
                    }}
                >
                    {params.value}
                </span>
            )
        },
        {
            headerName: "ACTIONS",
            field: "actions",
            width: 150,
            tooltipField: "actions",
            headerTooltip: "ACTIONS",
            cellRenderer: actionsCellRenderer,
            headerClass: 'custom-header',
            cellClass: 'text-center'
        },
        {
            headerName: "REPORTS",
            field: "reports",
            width: 130,
            tooltipField: "reports",
            headerTooltip: "REPORTS",
            cellRenderer: reportsCellRenderer,
            headerClass: 'custom-header',
            cellClass: 'text-center'
        }
    ];

    const onSearch = (event) => {

    }



    const handleIncludeArchiveChange = (e) => {
        setIncludeArchive(e.target.checked);
        intialCalls(); // Call your function here
    };

    const intialCalls = () => {
        console.log("Initial data fetch or update triggered");
        // Implement your actual logic
    };

    const onRefreshClick = () => {
        console.log("Refresh clicked");
        // Implement refresh logic here
    };

    const handleSave = () => {
        const formBody = {
            token: token,
            user_id: editUser.id,
            FirstName: editUser.firstName,
            LastName: editUser.lastName,
            Phone: editUser.phone || "",
            GenderID: parseInt(editUser.gender),
            RoleID: rolesList.find(role => role.type_name === editUser.userType)?.type_id || 0,
            SubscriptionID: subscriptionsList.find(sub => sub.Type === editUser.subscriptionType)?.Id || 0,
            Active: formData.active,
            LoginUserID: userData.user_id
        };

        console.log("Saving user data:", formBody);

        dispatch(usersManagementDetailsUpdate(formBody))
            .unwrap()
            .then(response => {
                console.log("Update successful:", response);

                setShowEditModal(false);
                setShowSuccessModal(true);
                setEditUser(null);

                // Refresh the users list
                return dispatch(getUserManagementDetailsGet(params));
            })
            .then((response) => {
                const Table = response.payload;
                if (Table && Array.isArray(Table)) {
                    const formattedData = Table.map(user => ({
                        id: user.UserID,
                        userName: user.UserName,
                        email: user.EmailAddress,
                        firstName: user.FirstName,
                        lastName: user.LastName,
                        phone: user.Phone,
                        userType: user.UserTypeName,
                        status: user.Status,
                        subscriptionType: user.SubscriptionType,
                        lastLoggedIn: user.LastLoggedIn,
                        accountNum: user.AccountNum,
                        address1: user.Address1,
                        address2: user.Address2,
                        city: user.City,
                        state: user.State,
                        zip: user.Zip,
                        country: user.Country,
                        dob: user.DOB,
                        gender: user.Gender,
                        imagePath: user.ImagePath,
                        locked: user.Locked
                    }));
                    setUsersData(formattedData);
                }
            })
            .catch(error => {
                console.error("Error updating user:", error);
            });
    };

    const handleConfirmDelete = () => {
        if (!userToDelete) return;
        const formBody = {
            UserID: userToDelete.id,
            LoginUserID: userData.user_id,
            token: token,
        };
        dispatch(usersManagementArchive(formBody))
            .unwrap()
            .then(response => {
                console.log("Archive successful:", response);
                setShowDeleteModal(false);
                setUserToDelete(null);
                setSuccessType("delete");
                setShowSuccessModal(true);
                // Refresh the users list after delete
                return dispatch(getUserManagementDetailsGet(params));
            })
            .then((response) => {
                const Table = response.payload;
                if (Table && Array.isArray(Table)) {
                    const formattedData = Table.map(user => ({
                        id: user.UserID,
                        userName: user.UserName,
                        email: user.EmailAddress,
                        firstName: user.FirstName,
                        lastName: user.LastName,
                        phone: user.Phone,
                        userType: user.UserTypeName,
                        status: user.Status,
                        subscriptionType: user.SubscriptionType,
                        lastLoggedIn: user.LastLoggedIn,
                        accountNum: user.AccountNum,
                        address1: user.Address1,
                        address2: user.Address2,
                        city: user.City,
                        state: user.State,
                        zip: user.Zip,
                        country: user.Country,
                        dob: user.DOB,
                        gender: user.Gender,
                        imagePath: user.ImagePath,
                        locked: user.Locked
                    }));
                    setUsersData(formattedData);
                }
            })
            .catch(error => {
                console.error("Error archiving user:", error);
                setShowDeleteModal(false);
                setUserToDelete(null);
            });
    };

    // Pagination calculation helpers
    const totalPages = Math.ceil(totalCount / pageSize);

    useEffect(() => {
        if (showSuccessModal) {
            showPmrSuccess('Operation completed successfully.').then(() => setShowSuccessModal(false));
        }
    }, [showSuccessModal]);

    return (
        <div className="bg-white rounded shadow-lg py-4 px-3">
            <div className="row">

                {loading ? (
                    <StethoscopeLoading message="Loading..." />
                ) : usersData.length > 0 ? (
                    <>
                        <div className="col-12 px-2 mb-3">
                            <CTSearchView
                                placeholder="Search users..."
                                onChange={onSearch}
                                onChangeclear={onClear}
                            />
                        </div>

                        <div className="d-flex align-items-center justify-content-end w-100 mb-2">

                            <span
                                onClick={onRefreshClick}
                                title="Refresh"
                                style={{ cursor: 'pointer' }}
                                className="d-flex align-items-center text-info"
                            >
                                <RotateCw size={18} style={{ marginRight: '0.5rem' }} />
                                Refresh
                            </span>
                        </div>

                        <div className="ag-theme-balham custom-ag-grid " style={{ height: 'calc(80vh - 200px)', width: '100%' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={usersData}
                                columnDefs={userColumns}
                                enableBrowserTooltips={true}
                                tooltipShowDelay={0}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                    minWidth: 80
                                }}
                                rowSelection="multiple"
                                pagination={true}
                                paginationPageSize={11}
                                suppressRowClickSelection={true}
                                headerHeight={44}
                                rowHeight={48}
                                onRowClicked={handleReportClick}
                            />
                        </div>


                        <PaginationFooter
                            currentPage={currentPage}
                            totalPages={totalPages}
                            selectedCount={selectedCount}
                            totalCount={totalCount}
                            onPageChange={(page) => {
                                if (page >= 1 && page <= totalPages) setCurrentPage(page);
                            }}
                        />
                    </>
                ) : (
                    <Card className="border-0 h-100">
                        <Card.Body className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh", height: "60vh" }}>
                            <p className="text-muted m-0">No users found.</p>
                        </Card.Body>
                    </Card>
                )}

                {showEditModal && editUser && (
                    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.3)' }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px', width: '50%' }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Edit User Details</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <Form>
                                        <Form.Group className="mb-2">
                                            <Form.Label>First Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={editUser.firstName}
                                                onChange={(e) => setEditUser({ ...editUser, firstName: e.target.value })}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Last Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={editUser.lastName}
                                                onChange={(e) => setEditUser({ ...editUser, lastName: e.target.value })}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Phone Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={editUser.phone}
                                                onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Gender</Form.Label>
                                            <Form.Select
                                                value={editUser.gender || ""}
                                                onChange={(e) => {
                                                    console.log("Selected gender:", e.target.value);
                                                    setEditUser({ ...editUser, gender: e.target.value });
                                                }}
                                                style={{
                                                    height: '45px',
                                                    border: '1px solid #ced4da',
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '4px'
                                                }}
                                                className="custom-select"
                                                placeholder="Gender"
                                            >
                                                {gendersList && gendersList.length > 0 ? (
                                                    gendersList.map((gender) => (
                                                        <option key={gender.gender_id} value={gender.gender_id}>{gender.name}</option>
                                                    ))
                                                ) : (
                                                    <option value="" disabled>Loading...</option>
                                                )}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Role</Form.Label>
                                            <Form.Select
                                                value={editUser.userType || ""}
                                                onChange={(e) => {
                                                    console.log("Selected role:", e.target.value);
                                                    setEditUser({ ...editUser, userType: e.target.value });
                                                }}
                                                style={{
                                                    height: '45px',
                                                    border: '1px solid #ced4da',
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '4px'
                                                }}
                                                className="custom-select"
                                                placeholder="Role"
                                            >
                                                {rolesList && rolesList.length > 0 ? (
                                                    rolesList.map((role) => (
                                                        <option key={role.type_id} value={role.type_name}>{role.type_name}</option>
                                                    ))
                                                ) : (
                                                    <option value="" disabled>Loading...</option>
                                                )}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Subscription</Form.Label>
                                            <Form.Select
                                                value={editUser.subscriptionType || ""}
                                                onChange={(e) => {
                                                    console.log("Selected subscription:", e.target.value);
                                                    setEditUser({ ...editUser, subscriptionType: e.target.value });
                                                }}
                                                style={{
                                                    height: '45px',
                                                    border: '1px solid #ced4da',
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '4px'
                                                }}
                                                className="custom-select"
                                                placeholder="Subscription"
                                            >
                                                {subscriptionsList && subscriptionsList.length > 0 ? (
                                                    subscriptionsList.map((subscription) => (
                                                        <option key={subscription.Id} value={subscription.Type}>{subscription.Type}</option>
                                                    ))
                                                ) : (
                                                    <option value="" disabled>Loading...</option>
                                                )}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-2">

                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="form-check form-switch">
                                                    <label className="form-check-label">Active</label>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        name="active"
                                                        checked={formData.active}
                                                        onChange={handleInputChange}
                                                        style={{
                                                            backgroundColor: formData.active ? "#003b87" : "#dee2e6",
                                                            borderColor: formData.active ? "#003b87" : "#dee2e6",
                                                            boxShadow: "none",
                                                            border: "none",
                                                            borderRadius: "10px",
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Form.Group>
                                    </Form>
                                </div>
                                <div className="modal-footer d-flex justify-content-center">
                                    <Button variant="primary" className="me-2" onClick={handleSave}>Save</Button>
                                    <Button variant="danger" onClick={() => setShowEditModal(false)}>Close</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showDeleteModal && userToDelete && (
                    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.3)' }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px', width: '90%' }}>
                            <div className="modal-content" style={{ borderRadius: 16 }}>
                                <div className="modal-body text-center py-5">
                                    <div style={{
                                        width: 80, height: 80, border: '2px solid #ffb300', borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto'
                                    }}>
                                        <span style={{ fontSize: 48, color: '#ffb300' }}>!</span>
                                    </div>
                                    <h4 className="mb-3" style={{ fontWeight: 600 }}>Are you sure?</h4>
                                    <div className="mb-3" style={{ color: '#555' }}>
                                        You want to delete the user <b>{userToDelete.userName}</b>.
                                    </div>
                                    <div className="d-flex justify-content-center gap-3">
                                        <Button
                                            variant="primary"
                                            style={{ minWidth: 80, background: '#003b87', border: 'none' }}
                                            onClick={handleConfirmDelete}
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            variant="danger"
                                            style={{ minWidth: 80 }}
                                            onClick={() => setShowDeleteModal(false)}
                                        >
                                            No
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Success Modal */}
                {/* The CustomSuccessModal component is removed, so this block is no longer needed. */}
            </div>
        </div>
    )
};


const PaginationFooter = ({ currentPage, totalPages, selectedCount, totalCount, onPageChange }) => {
    return (
        <div style={{
            borderTop: '1px solid #ddd',
            padding: '10px 15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: 'Arial, sans-serif',
            fontSize: 14,
            color: '#333',
            marginTop: 12
        }}>

            <div style={{ fontWeight: 600, color: '#003366' }}>
                {/* {selectedCount} selected / {totalCount} total */}
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>

                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        marginRight: 12,
                        border: 'none',
                        background: 'none',
                        padding: '0 6px',
                    }}
                    aria-label="Previous Page"
                >
                    <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 7H3M3 7L9 13M3 7L9 1" stroke="#003366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        style={{
                            cursor: 'pointer',
                            marginRight: 6,
                            fontWeight: pageNum === currentPage ? 700 : 400,
                            color: pageNum === currentPage ? '#003366' : '#666',
                            background: 'none',
                            border: 'none',
                            fontSize: 14,
                            padding: '4px 6px',
                            textDecoration: pageNum === currentPage ? 'underline' : 'none'
                        }}
                        aria-current={pageNum === currentPage ? "page" : undefined}
                        aria-label={`Page ${pageNum}`}
                    >
                        {pageNum}
                    </button>
                ))}

                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        marginLeft: 6,
                        border: 'none',
                        background: 'none',
                        padding: '0 6px',
                    }}
                    aria-label="Next Page"
                >
                    <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 7H15M15 7L9 1M15 7L9 13" stroke="#003366" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

        </div>
    );
};
export default UsersList;